import React, { useEffect, useState } from "react";
import { Amplify, Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import * as workerTimers from "worker-timers";
import { ToastContainer } from "react-toastify";
import AppRoutes from "./routes/AppRoutes";
import ErrorBoundary from "./utils/errorBoundary";
import AuthWrapper from "./components/wrappers/AuthWrapper";
import { addEmptyStringForNullValues, logout } from "./reusable/common";
import { useEvent } from "./contexts/EventContext";
import { getUserById } from "./crud/users.crud";
import { getSubRoute } from "./utils/core";
import { useUser } from "./contexts/AuthContext";
import { getEventDetails } from "./crud/event.crud";
import { setJwtToken } from "./crud/axios";
import { loggedinAwsConfig } from "./config/aws-exports";
import { ClipLoader } from "react-spinners";
import { spinnerStyle } from "./config/app";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import { getGalleryImagesPrivate } from "./crud/gallery.crud";
import { errors } from "./utils/constants";
import { NotificationBox } from "kh-design-system/components";

const App: React.FC = () => {
	const navigate = useNavigate(); // Hook for navigation
	const { user, setUser, setIsRegistered, setIsOrganizer, setLoading, loading } = useUser();
	const {
		eventDetails,
		setEventDetails,
		setEventLoading,
		setEventError,
		eventLoading,
		showPageLoader
	} = useEvent();
	const [isError, setIsError] = useState(false);
	const [toasterMessage, setToasterMessage] = useState("");
	const [notificationPopup, setNotificationPopup] = useState(false);

	const fetchEventDetails = async () => {
		try {
			setEventLoading(true);

			//Get the event details
			const eventResponse = await getEventDetails(getSubRoute());
			localStorage.setItem("eventId", eventResponse?.data?.event_id);
			//Set the event data to the event context
			setEventDetails({
				...eventResponse?.data,
				eventUrl: eventResponse?.data?.event_url,
				eventId: eventResponse?.data?.event_id
			});
			localStorage.setItem("eventId", eventResponse?.data?.event_id);
			localStorage.setItem("appLogo", eventResponse?.data?.att_app_logo);
			setEventLoading(false);
			setEventError(false);
		} catch (error: any) {
			setEventLoading(false);
			setEventError(true);
			setLoading(false);
			// Handle network/API failures and redirect to 500 page
			if (
				error.response?.status === 404 ||
				error.response?.data?.error === "Event does not exist or invalid gallery url"
			) {
				navigate("/error/404"); // Navigate to 404 page on not found error
			} else {
				navigate("/internal-error"); // Navigate to 500 page on server error or other failure
			}
		}
	};

	const checkAuthStatus = async () => {
		try {
			const userResponse = await Auth.currentAuthenticatedUser();
			const sessionResponse = await Auth.currentSession();
			if (sessionResponse && userResponse) {
				setUser((user: any) => ({
					...user,
					session: sessionResponse,
					user: userResponse
				}));
				setJwtToken(sessionResponse?.getIdToken()?.getJwtToken());
				Amplify.configure({
					...loggedinAwsConfig
				});
			}
		} catch (error) {
			console.log("User is not authenticated");
			setUser(false);
			setLoading(false);
		}
	};

	const refreshToken = async () => {
		workerTimers.setInterval(
			async () => {
				try {
					const sessionResponse: any = await Auth.currentSession();
					const userResponse = await Auth.currentAuthenticatedUser();
					if (sessionResponse && userResponse) {
						userResponse.refreshSession(
							sessionResponse.refreshToken,
							(err: any, session: any) => {
								if (err) throw new Error(err);
								if (session?.idToken) {
									const { idToken } = session;
									setJwtToken(idToken.jwtToken);
								}
							}
						);
					}
				} catch (error) {
					console.error("Error checking auth status:", error);
					console.log("User is not authenticated");
					setUser(false);
				}
			},
			60 * 45 * 1000
		);
	};

	const fetchAttendeeDetails = async () => {
		setLoading(true);
		try {
			if (eventDetails && user) {
				const response = await getGalleryImagesPrivate({
					eventId: eventDetails.event_id,
					limit: 1,
					offset: 0
				});
				if (response?.data) {
					setIsRegistered(true);
				}
				setLoading(false);
			}
		} catch (err: any) {
			console.log(err);
			logout();
			localStorage.removeItem("openFaceSearch");
			localStorage.removeItem("isFaceSearchClicked");
			localStorage.removeItem("openDownloadPopup");
			localStorage.removeItem("isDownloadClicked");
			setIsOrganizer(false);
			setIsRegistered(false);
			setLoading(false);
			if (err?.response?.data?.error === errors.USER_NOT_ALLOWED && !showPageLoader) {
				setIsError(true);
				setToasterMessage(errors.NOT_REGISTERED_FOR_EVENT);
				setNotificationPopup(true);
			}
		}
	};

	async function getProfileAndRegistrations() {
		try {
			const res = await getUserById();
			const userData = addEmptyStringForNullValues(res.data);
			localStorage.setItem("user", JSON.stringify(userData));
		} catch (error) {
			console.log("error: ", error);
		}
	}

	useEffect(() => {
		if (eventDetails) {
			// Set colors with precedence for query parameters
			document.documentElement.style.setProperty(
				"--primary-color",
				eventDetails?.gallery_brand_color || "#002E6E"
			);

			const font = `'${eventDetails?.gallery_font || "Figtree"}', sans-serif`;
			document.documentElement.style.setProperty("--font-family", font);

			localStorage.setItem("eventUrl", eventDetails.eventUrl);
		}
	}, [eventDetails]);

	useEffect(() => {
		checkAuthStatus();
		fetchEventDetails();
	}, []);

	useEffect(() => {
		if (user) {
			const getCurrentUser = async () => {
				const sessionResponse: any = await Auth.currentSession();
				setJwtToken(sessionResponse?.getIdToken()?.getJwtToken());
				//Start refreshing token every 1 hour
				refreshToken();
			};
			getCurrentUser();
			getProfileAndRegistrations();
		}
	}, [user]);

	useEffect(() => {
		if (eventDetails && user) {
			fetchAttendeeDetails();
		}
	}, [eventDetails, user]);

	useEffect(() => {
		if (notificationPopup) {
			const timer = setTimeout(() => {
				setIsError(false);
				setNotificationPopup(false);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [notificationPopup]);

	if ((eventLoading || loading) && showPageLoader)
		return (
			<div style={spinnerStyle}>
				<ClipLoader
					color="#4A90E2"
					loading={eventLoading || loading}
					size={50}
					aria-label="Loading Spinner"
				/>
			</div>
		);

	return (
		<ErrorBoundary>
			<ToastContainer />

			{notificationPopup && <NotificationBox message={toasterMessage} isError={isError} />}

			<div className="font h-100">
				<AuthWrapper>
					<AppRoutes />
				</AuthWrapper>
			</div>
		</ErrorBoundary>
	);
};

export default App;
